import { getConfig } from '@/lib/config';
import { friendlyMessage, isPlanUpgradeRequired, limitValues, type WireError } from '@/lib/errors/catalog';
import { wireFromBody, withRetryAfterHeader } from '@/lib/errors/http';
import { ErrorCode } from '@/lib/protobuf/gen/errors/errors_pb';

export class AuthError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    /** BILL-20a: the normalised wire error, so callers can branch on the typed
     *  code (e.g. isPlanUpgradeRequired(e.wire)). Set by apiFetch on every
     *  non-OK response; absent on AuthErrors built elsewhere. */
    public wire?: WireError,
    /** AON-08: the parsed JSON error body, set by apiFetch; undefined for a
     *  non-JSON body. Routes that refuse with structured detail (the
     *  always-on space's `reason` and `orgs`) are read from here. */
    public body?: unknown,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

let _tokenProvider: (() => string | null) | null = null;

/** Called once by authStore to wire up token injection. */
export function setTokenProvider(fn: () => string | null) {
  _tokenProvider = fn;
}

let _subscriptionRequiredHandler: (() => void) | null = null;

/**
 * BILL-03 — register the callback fired when any apiFetch is refused with
 * HTTP 402 + ERROR_CODE_SUBSCRIPTION_REQUIRED. The dashboard layout wires it
 * to `entitlementStore.markRequired`, so a tab whose verdict went stale swaps
 * to the paywall instead of surfacing a stream of refusals. Pass null to clear.
 * Keyed on BOTH status and code: 402 alone also means BUDGET_EXHAUSTED.
 */
export function setSubscriptionRequiredHandler(fn: (() => void) | null) {
  _subscriptionRequiredHandler = fn;
}

/**
 * Fire the BILL-03 subscription-required handler for a refusal read OUTSIDE
 * apiFetch — a raw-`fetch` caller (FormData uploads) never reaches the check
 * above, so without this an unsubscribed user sees that caller's own error
 * copy instead of the paywall.
 */
export function notifySubscriptionRequired(): void {
  _subscriptionRequiredHandler?.();
}

let _planUpgradeHandler: ((wire: WireError) => void) | null = null;

/**
 * BILL-20a — register the callback fired when any apiFetch is refused with
 * HTTP 403 + ERROR_CODE_PLAN_UPGRADE_REQUIRED (241). The dashboard layout wires
 * it to showPlanUpsellToast; a caller that ALSO surfaces the error through
 * toastStore.showError(e.wire) collapses onto the same toast (stable id per
 * feature, design D19/KD-S9). Pass null to clear. Keyed on BOTH status and code,
 * like the 402 handler above.
 */
export function setPlanUpgradeHandler(fn: ((wire: WireError) => void) | null) {
  _planUpgradeHandler = fn;
}

/** Returns Authorization header object for use with raw fetch() when apiFetch can't be used (e.g. FormData uploads, calls returning raw Response). */
export function getAuthHeaders(): Record<string, string> {
  const token = _tokenProvider?.();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch wrapper that prepends apiBase and injects Authorization header
 * when a token provider has been registered (set by authStore).
 * Does NOT handle token refresh — that's the caller's responsibility.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const { apiBase } = getConfig();
  const url = `${apiBase}${path}`;

  const token = _tokenProvider?.();
  const authHeaders: Record<string, string> = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  });

  if (!res.ok) {
    // R8 Phase 1b — route the error through the shared typed normalization
    // (lib/errors/http.ts) + the localized catalog, instead of surfacing the
    // raw `body.error` string. Legacy endpoints without `error_code` keep
    // their curated `error` string verbatim (wireFromBody only synthesizes a
    // status-derived code when there is neither code nor message). The body
    // is read ONCE — both the wire shape and the legacy `code` field come
    // from the same parse.
    let raw: unknown;
    try {
      const text = await res.text();
      if (text) {
        try {
          raw = JSON.parse(text) as unknown;
        } catch {
          // Non-JSON error body — status-derived fallback via wireFromBody.
        }
      }
    } catch {
      // Body read failed — status-derived fallback via wireFromBody.
    }
    const legacyCode =
      typeof raw === 'object' && raw !== null && typeof (raw as { code?: unknown }).code === 'string'
        ? (raw as { code: string }).code
        : undefined;
    const wire = withRetryAfterHeader(wireFromBody(raw, res.status), res.headers);
    if (res.status === 402 && wire.code === ErrorCode.SUBSCRIPTION_REQUIRED) {
      _subscriptionRequiredHandler?.();
    }
    if (res.status === 403 && isPlanUpgradeRequired(wire)) {
      // The handler runs synchronously inside apiFetch: a throw from it (a
      // toast regression) must not replace the AuthError the caller branches on.
      try {
        _planUpgradeHandler?.(wire);
      } catch (err) {
        console.error('[apiFetch] plan-upgrade handler failed', err);
      }
    }
    throw new AuthError(friendlyMessage(wire.code, wire.message, limitValues(wire)), res.status, legacyCode, wire, raw);
  }

  // Status-only successes (204, or Fiber's SendStatus which puts the plain-text
  // status message in the body, e.g. "Created") have no JSON to parse. Parsing
  // them used to throw AFTER the server had committed the mutation — the caller
  // then surfaced an error for an operation that succeeded (E1 P13 grant bug).
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  if (text.length === 0) {
    return undefined as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as T;
  }
}

/**
 * Fetch with Authorization header from provided token.
 */
export async function authFetch<T>(
  path: string,
  token: string,
  options: RequestInit = {},
): Promise<T> {
  return apiFetch<T>(path, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  });
}
